import type { CaptureResult } from 'posthog-js';

interface FrameLike {
  filename?: string;
  abs_path?: string;
  in_app?: boolean;
}

export type OrigemExcecao = 'dominio' | 'terceiro' | 'indeterminado';

export const PROP_ORIGEM_EXCECAO = 'excecao_origem';

const URL_HTTP = /^https?:\/\//i;
const ESQUEMA_EXTENSAO = /^(chrome|chrome-untrusted|moz|safari-web|safari|ms-browser|edge)-extension:\/\//i;

function alvoDoFrame(frame: FrameLike): string {
  return String(frame?.abs_path || frame?.filename || '').trim();
}

export function isFrameDoDominio(frame: FrameLike, origin: string): boolean {
  const alvo = alvoDoFrame(frame);
  if (!alvo) return false;
  if (origin && (alvo === origin || alvo.startsWith(`${origin}/`))) return true;
  if (frame?.in_app === true && !URL_HTTP.test(alvo) && !ESQUEMA_EXTENSAO.test(alvo)) return true;
  return false;
}

export function isFrameDeTerceiro(frame: FrameLike, origin: string): boolean {
  const alvo = alvoDoFrame(frame);
  if (!alvo) return false;
  if (isFrameDoDominio(frame, origin)) return false;
  return URL_HTTP.test(alvo) || ESQUEMA_EXTENSAO.test(alvo);
}

interface ExcecaoLike {
  stacktrace?: { frames?: FrameLike[] };
}

export function extrairFrames(cr: CaptureResult): FrameLike[] {
  const listaRaw = cr?.properties?.['$exception_list'];
  const lista = Array.isArray(listaRaw) ? (listaRaw as ExcecaoLike[]) : [];
  const frames: FrameLike[] = [];
  for (const exc of lista) {
    const fs = exc?.stacktrace?.frames;
    if (Array.isArray(fs)) frames.push(...fs);
  }
  return frames;
}

export function possuiFrameDoDominio(cr: CaptureResult, origin: string): boolean {
  return extrairFrames(cr).some((f) => isFrameDoDominio(f, origin));
}

export function classificarOrigemExcecao(cr: CaptureResult, origin: string): OrigemExcecao {
  const frames = extrairFrames(cr);
  if (frames.some((f) => isFrameDoDominio(f, origin))) return 'dominio';
  if (frames.length > 0 && frames.every((f) => isFrameDeTerceiro(f, origin))) return 'terceiro';
  return 'indeterminado';
}

function marcarOrigem(cr: CaptureResult, origem: OrigemExcecao): CaptureResult {
  try {
    const props = (cr.properties ?? {}) as Record<string, unknown>;
    props[PROP_ORIGEM_EXCECAO] = origem;
    (cr as { properties?: unknown }).properties = props;
  } catch {
    return cr;
  }
  return cr;
}

export function filtrarExcecaoBeforeSend(
  cr: CaptureResult | null,
  origin: string
): CaptureResult | null {
  if (!cr) return cr;
  if (cr.event !== '$exception') return cr;
  if (!origin) return cr;
  const origem = classificarOrigemExcecao(cr, origin);
  if (origem === 'terceiro') return null;
  return marcarOrigem(cr, origem);
}
